const padMediaIndex = (value) => String(value).padStart(2, '0');
const BNQ_RAIL_DEFAULT_SPEED = 60;
const BNQ_RAIL_MIN_SPEED = 20;
const BNQ_RAIL_MAX_SPEED = 120;
const BNQ_RAIL_MAX_FRAME_DELTA = 64;
const bnqEditorialRailPauseState = new Map();

class BnqEditorialRail extends HTMLElement {
  #abortController = new AbortController();
  #animationFrame = 0;
  #lastFrame = 0;
  #index = -1;
  #playing = false;
  #dragging = false;
  #dragMoved = false;
  #dragStartX = 0;
  #dragStartScroll = 0;
  #direction = 1;
  #maximum = 0;
  #position = 0;
  #itemOffsets = [];
  #measured = false;
  #speed = BNQ_RAIL_DEFAULT_SPEED;
  #measureFrame = 0;
  #resizeObserver = null;
  #itemObserver = null;
  #itemVisibility = new Map();
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #desktop = window.matchMedia('(min-width: 750px)');
  #userPaused = false;
  #resumeAfterVisibility = false;

  static get observedAttributes() {
    return ['data-speed'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-speed' && oldValue !== newValue) this.#speed = this.#parseSpeed(newValue);
  }

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const track = this.track;
    if (!track) return;

    this.#speed = this.#parseSpeed(this.dataset.speed);
    this.#userPaused = bnqEditorialRailPauseState.get(this.#stateKey) === true;

    this.querySelector('[data-bnq-rail-previous]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-bnq-rail-next]')?.addEventListener('click', () => this.#step(1), { signal });
    this.querySelector('[data-bnq-rail-play]')?.addEventListener('click', () => this.#togglePlayback(), { signal });
    this.querySelector('[data-bnq-rail-expand]')?.addEventListener('click', (event) => this.#openCurrent(event), { signal });

    track.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    track.addEventListener('pointermove', this.#handlePointerMove, { signal });
    track.addEventListener('pointerup', this.#handlePointerUp, { signal });
    track.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    track.addEventListener('click', this.#handleTrackClick, { signal, capture: true });
    track.addEventListener('load', this.#scheduleMeasure, { signal, capture: true });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    const slideshow = this.closest('media-gallery')?.querySelector('slideshow-component');
    slideshow?.addEventListener('slideshow:select', this.#handleMobileSelection, { signal });

    this.#reducedMotion.addEventListener('change', this.#handleMotionPreference, { signal });
    this.#desktop.addEventListener('change', this.#handleBreakpoint, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibility, { signal });
    this.#itemObserver = new IntersectionObserver(this.#handleItemIntersection, {
      root: track,
      threshold: [0.25, 0.5, 0.75, 0.99],
    });
    this.items.forEach((item) => this.#itemObserver?.observe(item));
    this.#resizeObserver = new ResizeObserver(this.#scheduleMeasure);
    this.#resizeObserver.observe(track);
    this.items.forEach((item) => this.#resizeObserver?.observe(item));
    this.#setIndex(0);
    this.#syncPlaybackButton();
    this.#scheduleMeasure();
  }

  disconnectedCallback() {
    this.#pause();
    this.#itemObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#measureFrame);
    this.#measureFrame = 0;
    this.#abortController.abort();
  }

  get track() {
    return this.querySelector('[data-bnq-rail-track]');
  }

  get items() {
    return Array.from(this.track?.querySelectorAll('[data-bnq-media-index]') ?? []);
  }

  get #stateKey() {
    return this.dataset.productId || '';
  }

  #parseSpeed(value) {
    const speed = Number(value);
    if (!Number.isFinite(speed)) return BNQ_RAIL_DEFAULT_SPEED;
    return Math.min(BNQ_RAIL_MAX_SPEED, Math.max(BNQ_RAIL_MIN_SPEED, speed));
  }

  #setUserPaused(paused) {
    this.#userPaused = paused;
    if (this.#stateKey) bnqEditorialRailPauseState.set(this.#stateKey, paused);
  }

  #scheduleMeasure = () => {
    cancelAnimationFrame(this.#measureFrame);
    this.#measureFrame = requestAnimationFrame(this.#measure);
  };

  #measure = () => {
    this.#measureFrame = 0;
    const track = this.track;
    if (!track) return;

    const previousMaximum = this.#maximum;
    const currentPosition = this.#playing ? this.#position : track.scrollLeft;
    const clientWidth = track.clientWidth;
    const maximum = Math.max(0, track.scrollWidth - clientWidth);

    this.#measured = clientWidth > 0;
    this.#maximum = maximum;
    this.#itemOffsets = this.items.map((item) => item.offsetLeft);

    if (previousMaximum > 1 && maximum > 1) {
      this.#position = Math.min(maximum, Math.max(0, (currentPosition / previousMaximum) * maximum));
    } else {
      this.#position = Math.min(maximum, Math.max(0, currentPosition));
    }

    track.scrollLeft = this.#position;
    this.#syncFromPosition();

    if (!this.#canPlay) this.#pause();
    else if (!this.#userPaused) this.#start();
    else this.#syncPlaybackButton();
  };

  #step(delta) {
    this.#setUserPaused(true);
    this.#pause();
    const count = this.items.length;
    if (count <= 1) return;
    this.#select((this.#index + delta + count) % count);
  }

  #select(index, behavior = 'smooth') {
    const track = this.track;
    const item = this.items[index];
    if (!track || !item) return;
    this.toggleAttribute('data-free-position', false);
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    track.scrollTo({ left: Math.min(item.offsetLeft, maximum), behavior });
    this.#setIndex(index);
  }

  #setIndex(index) {
    const total = this.items.length;
    if (!total) return;
    const nextIndex = Math.max(0, Math.min(index, total - 1));
    if (nextIndex === this.#index) return;
    this.#index = nextIndex;
    const current = this.querySelector('[data-bnq-rail-current]');
    if (current) current.textContent = padMediaIndex(this.#index + 1);
    this.#syncMobileProgress(this.#index);
    this.items.forEach((item, itemIndex) => item.toggleAttribute('data-bnq-current', itemIndex === this.#index));
  }

  #syncFromScroll() {
    const track = this.track;
    if (!track) return;
    this.#position = Math.min(this.#maximum, Math.max(0, track.scrollLeft));
    this.#syncFromPosition();
  }

  #syncFromPosition() {
    if (this.#maximum > 0 && this.#position >= this.#maximum - 1) {
      this.#setIndex(this.items.length - 1);
      return;
    }
    const index = this.#itemOffsets.reduce((closestIndex, offset, itemIndex) => {
      const currentDistance = Math.abs(offset - this.#position);
      const closestDistance = Math.abs(this.#itemOffsets[closestIndex] - this.#position);
      return currentDistance < closestDistance ? itemIndex : closestIndex;
    }, 0);
    this.#setIndex(index);
  }

  #togglePlayback() {
    if (this.#playing) {
      this.#setUserPaused(true);
      this.#pause();
      return;
    }
    this.#setUserPaused(false);
    this.#start();
  }

  get #canPlay() {
    return this.#desktop.matches && !this.#reducedMotion.matches && this.#measured && this.#maximum > 1;
  }

  #start() {
    if (!this.#canPlay || this.#playing) return;
    if (document.hidden) {
      this.#resumeAfterVisibility = !this.#userPaused;
      this.#syncPlaybackButton();
      return;
    }
    const track = this.track;
    if (!track) return;
    this.toggleAttribute('data-free-position', false);
    this.#position = Math.min(this.#maximum, Math.max(0, track.scrollLeft));
    this.#playing = true;
    this.toggleAttribute('data-playing', true);
    this.#lastFrame = performance.now();
    this.#syncPlaybackButton();
    this.#animationFrame = requestAnimationFrame(this.#tick);
  }

  #pause() {
    const wasPlaying = this.#playing;
    this.#playing = false;
    if (wasPlaying) this.toggleAttribute('data-free-position', true);
    this.toggleAttribute('data-playing', false);
    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = 0;
    this.#syncPlaybackButton();
  }

  #tick = (time) => {
    const track = this.track;
    if (!this.#playing || !track) return;
    const elapsed = Math.min(BNQ_RAIL_MAX_FRAME_DELTA, Math.max(0, time - this.#lastFrame)) / 1000;
    this.#lastFrame = time;

    let nextPosition = this.#position + this.#direction * this.#speed * elapsed;
    while (nextPosition > this.#maximum || nextPosition < 0) {
      if (nextPosition > this.#maximum) {
        nextPosition = this.#maximum * 2 - nextPosition;
        this.#direction = -1;
      } else {
        nextPosition = -nextPosition;
        this.#direction = 1;
      }
    }

    this.#position = nextPosition;
    track.scrollLeft = this.#position;
    this.#syncFromPosition();
    this.#animationFrame = requestAnimationFrame(this.#tick);
  };

  #syncPlaybackButton() {
    const button = this.querySelector('[data-bnq-rail-play]');
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = !this.#canPlay;
    button.setAttribute('aria-pressed', String(this.#playing));
    button.setAttribute('aria-label', this.#playing ? 'Pause media rail' : 'Play media rail');
  }

  #openCurrent(event) {
    this.#setUserPaused(true);
    this.#pause();
    const gallery = this.closest('media-gallery');
    if (typeof gallery?.zoom === 'function') gallery.zoom(this.#index, event);
  }

  #handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    const track = this.track;
    if (!track) return;
    this.#setUserPaused(true);
    this.#pause();
    this.#dragging = true;
    this.#dragMoved = false;
    this.#dragStartX = event.clientX;
    this.#dragStartScroll = track.scrollLeft;
    this.toggleAttribute('data-dragging', true);
  };

  #handlePointerMove = (event) => {
    if (!this.#dragging || event.pointerType === 'touch') return;
    const track = this.track;
    if (!track) return;
    const delta = event.clientX - this.#dragStartX;
    if (!this.#dragMoved && Math.abs(delta) <= 5) return;
    if (!this.#dragMoved) {
      this.#dragMoved = true;
      track.setPointerCapture(event.pointerId);
    }
    track.scrollLeft = this.#dragStartScroll - delta;
  };

  #handlePointerUp = (event) => {
    if (!this.#dragging || event.pointerType === 'touch') return;
    this.#dragging = false;
    this.toggleAttribute('data-dragging', false);
    this.toggleAttribute('data-free-position', true);
    if (this.track?.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.#syncFromScroll();
    if (this.#dragMoved) {
      setTimeout(() => {
        this.#dragMoved = false;
      }, 0);
    }
  };

  #handleTrackClick = (event) => {
    if (this.#dragMoved) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#dragMoved = false;
      return;
    }

    const zoomButton = event.target instanceof Element
      ? event.target.closest('.product-media-container__zoom-button')
      : null;
    const item = zoomButton?.closest('[data-bnq-media-index]');
    const index = Number(item?.dataset.bnqMediaIndex);
    if (!(zoomButton instanceof HTMLButtonElement) || !Number.isInteger(index)) return;

    this.#setUserPaused(true);
    this.#pause();
    this.#setIndex(index);
  };

  #handleItemIntersection = (entries) => {
    entries.forEach((entry) => this.#itemVisibility.set(entry.target, entry.intersectionRatio));
    if (this.#playing || this.#dragging) return;
    const track = this.track;
    if (track && this.#maximum > 0 && track.scrollLeft >= this.#maximum - 1) {
      this.#setIndex(this.items.length - 1);
      return;
    }
    const mostVisible = this.items.reduce((winner, item) => {
      const ratio = this.#itemVisibility.get(item) ?? 0;
      const winnerRatio = this.#itemVisibility.get(winner) ?? 0;
      return ratio > winnerRatio ? item : winner;
    }, this.items[this.#index] || this.items[0]);
    const index = this.items.indexOf(mostVisible);
    if (index >= 0) this.#setIndex(index);
  };

  #handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    }
  };

  #handleMobileSelection = (event) => {
    const index = Number(event.detail?.index);
    if (!Number.isNaN(index)) this.#syncMobileProgress(index);
  };

  #syncMobileProgress(index) {
    const total = this.items.length;
    const current = this.querySelector('[data-bnq-mobile-current]');
    if (current) current.textContent = padMediaIndex(index + 1);
    const slots = Array.from(this.querySelectorAll('[data-bnq-progress-slot]'));
    const visibleSlots = Math.min(4, total);
    const activeSlot = total <= 4 ? index : Math.round((index * 3) / Math.max(1, total - 1));
    slots.forEach((slot, slotIndex) => {
      slot.hidden = slotIndex >= visibleSlots;
      slot.toggleAttribute('data-active', slotIndex === activeSlot);
    });
  }

  #handleMotionPreference = () => {
    if (this.#reducedMotion.matches) {
      this.#resumeAfterVisibility = false;
      this.#pause();
    } else if (!this.#userPaused) {
      this.#start();
    }
  };

  #handleBreakpoint = () => {
    if (!this.#desktop.matches) this.#pause();
    else this.#scheduleMeasure();
  };

  #handleVisibility = () => {
    if (document.hidden) {
      this.#resumeAfterVisibility = this.#playing && !this.#userPaused;
      this.#pause();
    } else if (!this.#userPaused && (this.#resumeAfterVisibility || !this.#playing)) {
      this.#resumeAfterVisibility = false;
      this.#start();
    }
  };
}

class BnqPdpStory extends HTMLElement {
  #abortController = new AbortController();
  #breakpoint = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#syncMode();
    this.#breakpoint.addEventListener('change', this.#syncMode, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #syncMode = () => {
    const mode = this.#breakpoint.matches ? this.dataset.storyModeDesktop : this.dataset.storyModeMobile;
    const activeMode = mode || 'hidden';
    this.dataset.storyMode = activeMode;
    this.hidden = activeMode === 'hidden';
    this.querySelectorAll('[data-bnq-story-visual]').forEach((panel) => {
      panel.hidden = panel.dataset.bnqStoryVisual !== activeMode;
    });
  };
}

class BnqProductMotion extends HTMLElement {
  #abortController = new AbortController();
  #activePoster = '';
  #activeSourceSignature = '';
  #autoPaused = false;
  #autoplayAttempted = false;
  #autoplayRejected = false;
  #breakpoint = window.matchMedia('(min-width: 750px)');
  #connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  #failed = false;
  #failedSources = new Set();
  #nearViewport = false;
  #observer = null;
  #playPending = false;
  #prepared = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #saveData = Boolean(this.#connection?.saveData);
  #sourceVersion = 0;
  #syncFrame = 0;
  #toggleButton = null;
  #userActivated = false;
  #userPaused = false;
  #video = null;
  #visible = false;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const video = this.querySelector('[data-bnq-motion-video]');
    const toggle = this.querySelector('[data-bnq-motion-toggle]');
    if (!(video instanceof HTMLVideoElement) || !(toggle instanceof HTMLButtonElement)) return;

    const { signal } = this.#abortController;
    this.#video = video;
    this.#toggleButton = toggle;
    this.#observer?.disconnect();
    this.#activePoster = video.getAttribute('poster') || '';
    this.#activeSourceSignature = this.#sourceSignature(video.querySelectorAll('source'));
    this.#autoPaused = false;
    this.#autoplayAttempted = false;
    this.#autoplayRejected = false;
    this.#failed = false;
    this.#failedSources.clear();
    this.#nearViewport = false;
    this.#playPending = false;
    this.#prepared = video.querySelector('source') !== null;
    this.#saveData = Boolean(this.#connection?.saveData);
    this.#userActivated = false;
    this.#userPaused = false;
    this.#visible = false;

    video.autoplay = false;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = false;
    this.toggleAttribute('data-prepared', this.#prepared);
    this.#setState(toggle, !video.paused && !video.ended);
    this.#syncAvailability(video, toggle);

    toggle.addEventListener('click', this.#handleToggle, { signal });
    video.addEventListener('ended', () => {
      if (!video.loop || video.paused) this.#setState(toggle, false);
    }, { signal });
    video.addEventListener('pause', () => this.#setState(toggle, false), { signal });
    video.addEventListener('play', () => this.#setState(toggle, true), { signal });
    video.addEventListener('playing', () => this.#setState(toggle, true), { signal });
    video.addEventListener('error', this.#handleMediaError, { signal });
    this.#breakpoint.addEventListener('change', this.#handleBreakpointChange, { signal });
    this.#reducedMotion.addEventListener('change', this.#handleMotionPreferenceChange, { signal });
    this.#connection?.addEventListener?.('change', this.#handleConnectionChange, { signal });
    document.addEventListener('visibilitychange', this.#handleDocumentVisibility, { signal });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#handleIntersection, {
        rootMargin: '400px 0px',
        threshold: Array.from({ length: 101 }, (_value, index) => index / 100),
      });
      this.#observer.observe(this);
    } else {
      window.addEventListener('scroll', this.#handleFallbackPosition, { passive: true, signal });
      window.addEventListener('resize', this.#handleFallbackPosition, { passive: true, signal });
      this.#handleFallbackPosition();
    }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#syncFrame);
    this.#observer?.disconnect();
    this.#video?.pause();
    this.#abortController.abort();
  }

  #handleIntersection = ([entry]) => {
    if (!entry) return;
    this.#nearViewport = entry.isIntersecting;
    this.#visible = entry.isIntersecting && this.#isVisiblyIntersecting(entry.boundingClientRect);
    this.#syncRuntime();
  };

  #handleFallbackPosition = () => {
    const rect = this.getBoundingClientRect();
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    this.#nearViewport = rect.bottom > -400 && rect.top < viewportHeight + 400;
    this.#visible = this.#isVisiblyIntersecting(rect);
    this.#syncRuntime();
  };

  #handleBreakpointChange = () => {
    cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = requestAnimationFrame(() => {
      if (!(this.#video instanceof HTMLVideoElement) || !(this.#toggleButton instanceof HTMLButtonElement)) return;
      this.#syncAvailability(this.#video, this.#toggleButton);

      if (!this.#isActive()) {
        this.#pauseAutomatically(this.#video);
        return;
      }

      const rect = this.getBoundingClientRect();
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      this.#nearViewport = rect.bottom > -400 && rect.top < viewportHeight + 400;
      this.#visible = this.#isVisiblyIntersecting(rect);
      this.#syncRuntime();
    });
  };

  #handleMediaError = () => {
    if (this.#failed) return;
    this.#failed = true;
    this.#sourceVersion += 1;
    this.#video?.pause();
    this.#video?.replaceChildren();
    this.#setState(this.#toggleButton, false);
    this.#toggleButton.hidden = true;
    this.setAttribute('data-media-error', 'true');

    const media = this.closest('.bnq-product-media');
    const badge = media?.querySelector('[data-bnq-media-badge]');
    if (badge instanceof HTMLElement) {
      if (badge.dataset.fallbackBadge === 'static' && badge.dataset.staticLabel) {
        badge.textContent = badge.dataset.staticLabel;
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }

    const fallback = this.querySelector('[data-bnq-motion-fallback]');
    if (fallback instanceof HTMLElement && this.#video instanceof HTMLVideoElement) {
      this.#video.hidden = true;
      fallback.hidden = false;
      return;
    }

    const viewport = this.closest('[data-bnq-media-viewport]');
    const story = this.closest('bnq-pdp-story');
    if (viewport instanceof HTMLElement) viewport.hidden = true;
    if (story instanceof HTMLElement) {
      const breakpoint = this.dataset.bnqMotionBreakpoint || (this.#breakpoint.matches ? 'desktop' : 'mobile');
      story.dataset[`mediaUnavailable${breakpoint[0].toUpperCase()}${breakpoint.slice(1)}`] = 'true';
    }
  };

  #handleSourceError = (event) => {
    if (!(event.currentTarget instanceof HTMLSourceElement) || this.#failed) return;
    this.#failedSources.add(event.currentTarget);
    const sourceCount = this.#video?.querySelectorAll('source').length || 0;
    if (sourceCount > 0 && this.#failedSources.size >= sourceCount) this.#handleMediaError();
  };

  #handleMotionPreferenceChange = () => {
    if (this.#reducedMotion.matches && !this.#userActivated) {
      this.#pauseAutomatically(this.#video);
    } else {
      this.#syncRuntime();
    }
  };

  #handleConnectionChange = () => {
    this.#saveData = Boolean(this.#connection?.saveData);
    if (this.#saveData && !this.#userActivated) {
      this.#pauseAutomatically(this.#video);
    } else {
      this.#syncRuntime();
    }
  };

  #handleDocumentVisibility = () => {
    if (document.hidden) {
      this.#pauseAutomatically(this.#video);
    } else {
      this.#syncRuntime();
    }
  };

  #handleToggle = () => {
    if (!(this.#video instanceof HTMLVideoElement) || !(this.#toggleButton instanceof HTMLButtonElement)) return;
    void this.#toggle(this.#video, this.#toggleButton);
  };

  #syncRuntime() {
    const video = this.#video;
    const toggle = this.#toggleButton;
    if (!(video instanceof HTMLVideoElement) || !(toggle instanceof HTMLButtonElement)) return;

    this.#syncAvailability(video, toggle);
    if (!this.#isActive()) {
      this.#pauseAutomatically(video);
      return;
    }

    const media = this.#resolveMedia();
    if (this.#nearViewport) this.#installPoster(video, media.poster);
    if (this.#nearViewport && (!this.#saveData || media.sources.length === 0)) {
      this.#prepare(video, toggle);
    }

    if (this.#visible) {
      this.#maybePlay(video, toggle);
    } else {
      this.#pauseAutomatically(video);
    }
  }

  #resolveMedia() {
    const breakpoint = this.dataset.bnqMotionBreakpoint || (this.#breakpoint.matches ? 'desktop' : 'mobile');
    const template = this.querySelector('[data-bnq-motion-sources]');
    const sources = template instanceof HTMLTemplateElement
      ? Array.from(template.content.querySelectorAll('source'))
      : [];
    const poster = this.#video?.dataset.poster || '';

    return {
      breakpoint,
      poster,
      sources,
      sourceSignature: this.#sourceSignature(sources),
    };
  }

  #sourceSignature(sources) {
    return JSON.stringify(Array.from(sources, (source) => [
      source.getAttribute('src') || '',
      source.getAttribute('type') || '',
      source.getAttribute('media') || '',
    ]));
  }

  #syncAvailability(video, toggle) {
    const { sources } = this.#resolveMedia();
    toggle.hidden = this.#failed || sources.length === 0;
  }

  #prepare(video, toggle, { force = false } = {}) {
    if (this.#failed) return false;
    const media = this.#resolveMedia();
    toggle.hidden = media.sources.length === 0;

    if (media.sources.length > 0 && this.#saveData && !force) return false;

    const sameSource = this.#prepared && media.sourceSignature === this.#activeSourceSignature;
    if (sameSource) {
      this.#installPoster(video, media.poster);
      return true;
    }

    const wasPlaying = !video.paused && !video.ended;
    const shouldResume = !this.#userPaused && (wasPlaying || this.#autoPaused);
    if (wasPlaying) this.#autoPaused = true;

    video.pause();
    this.#setState(toggle, false);
    this.#sourceVersion += 1;
    const installedSources = media.sources.map((source) => source.cloneNode(true));
    this.#failedSources.clear();
    installedSources.forEach((source) => {
      source.addEventListener('error', this.#handleSourceError, {
        once: true,
        signal: this.#abortController.signal,
      });
    });
    video.replaceChildren(...installedSources);
    this.#installPoster(video, media.poster);

    this.#activeSourceSignature = media.sourceSignature;
    this.#prepared = true;
    this.toggleAttribute('data-prepared', true);

    if (media.sources.length === 0) {
      this.#autoPaused = false;
      return true;
    }

    video.load();
    if (!this.#userActivated && !this.#userPaused) {
      this.#autoplayAttempted = false;
      this.#autoplayRejected = false;
    }

    if (shouldResume && this.#visible) this.#autoPaused = true;
    return true;
  }

  #installPoster(video, poster) {
    if (poster === this.#activePoster && video.getAttribute('poster') === poster) return;

    if (poster) {
      video.poster = poster;
    } else {
      video.removeAttribute('poster');
    }
    this.#activePoster = poster;
  }

  #canAutoplay() {
    return this.#prepared
      && this.#visible
      && this.#isActive()
      && !document.hidden
      && !this.#saveData
      && !this.#reducedMotion.matches
      && !this.#autoplayRejected
      && !this.#userPaused;
  }

  #maybePlay(video, toggle) {
    if (!this.#canAutoplay() || !video.querySelector('source') || this.#playPending) return;

    if (this.#autoPaused) {
      void this.#requestPlayback(video, toggle, true);
      return;
    }

    if (!this.#autoplayAttempted && !this.#autoplayRejected) {
      this.#autoplayAttempted = true;
      video.loop = false;
      void this.#requestPlayback(video, toggle, true);
    }
  }

  #pauseAutomatically(video) {
    if (!(video instanceof HTMLVideoElement) || video.paused) return;
    this.#autoPaused = true;
    video.pause();
  }

  async #toggle(video, toggle) {
    this.#autoplayAttempted = true;
    this.#autoplayRejected = false;

    if (video.paused) {
      this.#userPaused = false;
      this.#autoPaused = false;
      if (!this.#prepare(video, toggle, { force: true }) || !video.querySelector('source')) return;

      const wasUserActivated = this.#userActivated;
      video.loop = video.dataset.loopAfterActivation === 'true' && !this.#reducedMotion.matches;
      const played = await this.#requestPlayback(video, toggle, false);
      if (played) {
        this.#userActivated = true;
      } else {
        this.#userActivated = wasUserActivated;
        video.loop = wasUserActivated && video.dataset.loopAfterActivation === 'true' && !this.#reducedMotion.matches;
      }
    } else {
      this.#userPaused = true;
      this.#autoPaused = false;
      video.pause();
    }
  }

  async #requestPlayback(video, toggle, isAutoplay) {
    if (this.#playPending) return false;
    const sourceVersion = this.#sourceVersion;
    this.#playPending = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    try {
      await video.play();
      if (sourceVersion !== this.#sourceVersion) return false;
      this.#autoPaused = false;
      return true;
    } catch (_error) {
      if (sourceVersion === this.#sourceVersion) {
        if (isAutoplay) this.#autoplayRejected = true;
        this.#setState(toggle, false);
      }
      return false;
    } finally {
      this.#playPending = false;
    }
  }

  #isVisiblyIntersecting(rect) {
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    return rect.bottom > 0 && rect.top < viewportHeight;
  }

  #isActive() {
    const story = this.closest('bnq-pdp-story');
    const panel = this.closest('[data-bnq-story-visual]');
    const expectedBreakpoint = this.dataset.bnqMotionBreakpoint;
    const activeBreakpoint = this.#breakpoint.matches ? 'desktop' : 'mobile';
    return story?.hidden !== true
      && story?.dataset.storyMode === 'product_in_motion'
      && panel?.hidden !== true
      && (!expectedBreakpoint || expectedBreakpoint === activeBreakpoint)
      && !this.#failed;
  }

  #setState(toggle, playing) {
    this.toggleAttribute('data-playing', playing);
    toggle.setAttribute('aria-pressed', String(playing));
    toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  }
}

class BnqPdpHandoffController extends HTMLElement {
  #abortController = new AbortController();
  #heroObserver = null;
  #mutationObserver = null;
  #dialogObserver = null;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const section = this.closest('.shopify-section');
    const sticky = section?.querySelector('[data-bnq-pdp-sticky]');
    const panel = section?.querySelector('[data-bnq-commerce-panel]');
    if (!(sticky instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
    const { signal } = this.#abortController;

    const panelRect = panel.getBoundingClientRect();
    sticky.dataset.bnqHeroPassed = String(panelRect.bottom <= 0);

    this.#heroObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      sticky.dataset.bnqHeroPassed = String(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
    });
    this.#heroObserver.observe(panel);

    sticky.addEventListener('click', (event) => this.#handleProxyClick(event, section), { signal, capture: true });
    this.#mutationObserver = new MutationObserver(() => this.#syncActions(section, sticky));
    this.#mutationObserver.observe(section, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-pressed', 'data-eligible'] });
    this.#dialogObserver = new MutationObserver(() => this.#syncDialogState(sticky));
    this.#dialogObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
    this.#syncActions(section, sticky);
    this.#syncDialogState(sticky);
  }

  disconnectedCallback() {
    this.#heroObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#dialogObserver?.disconnect();
    this.#abortController.abort();
  }

  #syncActions(section, sticky) {
    const commerce = section.querySelector('[data-bnq-commerce-panel]');
    const add = commerce?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    const stoq = commerce?.querySelector('.restock-rocket-button');
    const notify = sticky.querySelector('[data-bnq-sticky-notify]');
    const wishlist = sticky.querySelector('[data-bnq-sticky-wishlist]');
    const swym = commerce?.querySelector('#swym-atw-pdp-button');
    const notifyReady = add instanceof HTMLButtonElement && add.disabled && stoq instanceof HTMLElement;

    const notifyReadyValue = String(notifyReady);
    if (sticky.dataset.bnqNotifyReady !== notifyReadyValue) sticky.dataset.bnqNotifyReady = notifyReadyValue;
    if (notify instanceof HTMLButtonElement) {
      if (notify.hidden === notifyReady) notify.hidden = !notifyReady;
      const notifyLabel = stoq?.textContent?.trim() || 'Notify When Available';
      if (notifyReady && notify.textContent !== notifyLabel) notify.textContent = notifyLabel;
    }
    if (wishlist instanceof HTMLButtonElement) {
      const wishlistHidden = !(swym instanceof HTMLElement);
      if (wishlist.hidden !== wishlistHidden) wishlist.hidden = wishlistHidden;
      const pressed = swym?.classList.contains('swym-added') || swym?.getAttribute('aria-pressed') === 'true';
      const pressedValue = String(Boolean(pressed));
      if (wishlist.getAttribute('aria-pressed') !== pressedValue) wishlist.setAttribute('aria-pressed', pressedValue);
    }
    const availability = sticky.querySelector('[data-bnq-handoff-availability-text]');
    const sourceAvailability = commerce?.querySelector('.product-inventory__text');
    const availabilityText = sourceAvailability?.textContent?.trim();
    if (availability && availabilityText && availability.textContent !== availabilityText) availability.textContent = availabilityText;
  }

  #handleProxyClick(event, section) {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-bnq-sticky-notify]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!document.querySelector('.restock-rocket-wrapper')) section.querySelector('[data-bnq-commerce-panel] .restock-rocket-button')?.click();
    } else if (target?.closest('[data-bnq-sticky-wishlist]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      section.querySelector('[data-bnq-commerce-panel] #swym-atw-pdp-button')?.click();
    }
  }

  #syncDialogState(sticky) {
    const dialogOpen = Boolean(document.querySelector('dialog[open], .restock-rocket-wrapper'));
    sticky.dataset.bnqDialogOpen = String(dialogOpen);
  }
}

if (!customElements.get('bnq-editorial-rail')) customElements.define('bnq-editorial-rail', BnqEditorialRail);
if (!customElements.get('bnq-pdp-story')) customElements.define('bnq-pdp-story', BnqPdpStory);
if (!customElements.get('bnq-product-motion')) customElements.define('bnq-product-motion', BnqProductMotion);
if (!customElements.get('bnq-pdp-handoff-controller')) customElements.define('bnq-pdp-handoff-controller', BnqPdpHandoffController);
